import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DownloadIcon } from '@phosphor-icons/react';

interface DownloadModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (filename: string) => void;
  defaultFilename: string;
  title?: string;
  description?: string;
}

export const DownloadModal: React.FC<DownloadModalProps> = ({
  open,
  onClose,
  onDownload,
  defaultFilename,
  title = 'Download Report',
  description = 'Choose a filename for your download',
}) => {
  const [filename, setFilename] = useState(defaultFilename);

  // Update filename when defaultFilename changes
  useEffect(() => {
    setFilename(defaultFilename);
  }, [defaultFilename]);

  const handleDownload = () => {
    onDownload(filename);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleDownload();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="filename">Filename</Label>
            <Input
              id="filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter filename..."
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              A timestamp will be automatically added to the filename
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleDownload}>
            <DownloadIcon className="h-4 w-4 mr-2" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
